/**
 * components/clients/MergeClientsModal.tsx
 *
 * 3-step wizard for manually merging two client records.
 * Step 1 - Identification: select duplicate + confirm which record to keep.
 * Step 2 - Résolution: explicit radio-button choice for each conflicting field (skipped if no conflicts).
 * Step 3 - Confirmation: preview of the final merged client before executing.
 * Created Apr 2026.
 * Fix Apr 2026: handleConfirm awaits onMerge result and only closes on success;
 *   on failure the modal stays open with an error message.
 *   handleChoose field parameter typed as keyof FieldResolutions for safety.
 */

import { useState, useMemo } from 'react';
import { ArrowLeftRight, ShieldCheck } from 'lucide-react';
import { Modal } from '../ui/Modal';
import Button from '../ui/Button';
import ClientSelectorForMerge from './ClientSelectorForMerge';
import FieldConflictRow from './FieldConflictRow';
import ClientAvatar from '../ui/ClientAvatar';
import type { Database } from '../../types/database';
import type { FieldConflict, FieldResolutions, FieldResolutionChoice } from '../../types/merge';

type Client = Database['public']['Tables']['clients']['Row'];

type Step = 'identification' | 'resolution' | 'confirmation';

interface MergeClientsModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetClient: Client;
  userId: string;
  detectConflicts: (target: Client, source: Client) => FieldConflict[];
  onMerge: (targetId: string, sourceId: string, resolutions: FieldResolutions) => Promise<string | null>;
  merging: boolean;
}

function ClientCard({ client, role }: { client: Client; role: string }) {
  const name = [client.last_name, client.first_name].filter(Boolean).join(' ') || client.email || 'Client';
  return (
    <div className="flex-1 min-w-0 border border-neutral-200 rounded-xl p-4 bg-neutral-50">
      <p className="text-xs font-medium text-neutral-400 uppercase tracking-wide mb-2">{role}</p>
      <div className="flex items-center gap-3 mb-3">
        <ClientAvatar displayName={name} size="sm" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-neutral-900 truncate">{name}</p>
        </div>
      </div>
      <dl className="space-y-1.5 text-sm">
        {[
          ['Email', client.email],
          ['Téléphone', client.phone],
          ['Notes', client.notes],
        ].map(([label, value]) =>
          value ? (
            <div key={label as string} className="flex gap-2">
              <dt className="text-neutral-400 w-20 flex-shrink-0">{label}</dt>
              <dd className="text-neutral-700 truncate">{value}</dd>
            </div>
          ) : null
        )}
      </dl>
    </div>
  );
}

export default function MergeClientsModal({
  isOpen,
  onClose,
  targetClient,
  userId,
  detectConflicts,
  onMerge,
  merging,
}: MergeClientsModalProps) {
  const [step, setStep] = useState<Step>('identification');
  const [sourceClient, setSourceClient] = useState<Client | null>(null);
  const [keepTarget, setKeepTarget] = useState(true);
  const [resolutions, setResolutions] = useState<FieldResolutions>({});
  const [mergeError, setMergeError] = useState<string | null>(null);

  const effectiveTarget = keepTarget ? targetClient : sourceClient;
  const effectiveSource = keepTarget ? sourceClient : targetClient;

  const conflicts = useMemo<FieldConflict[]>(() => {
    if (!effectiveTarget || !effectiveSource) return [];
    return detectConflicts(effectiveTarget, effectiveSource);
  }, [effectiveTarget, effectiveSource, detectConflicts]);

  const resolvedCount = Object.keys(resolutions).length;
  const allResolved = resolvedCount === conflicts.length;

  const handleChoose = (field: keyof FieldResolutions, choice: FieldResolutionChoice) => {
    setResolutions((prev) => ({ ...prev, [field]: choice }));
  };

  const handleSelectSource = (client: Client) => {
    setSourceClient(client);
    setResolutions({});
  };

  const handleSwap = () => {
    setKeepTarget((v) => !v);
    setResolutions({});
  };

  const goToResolution = () => {
    setResolutions({});
    if (conflicts.length > 0) {
      setStep('resolution');
    } else {
      setStep('confirmation');
    }
  };

  const handleConfirm = async () => {
    if (!effectiveTarget || !effectiveSource) return;
    setMergeError(null);
    const result = await onMerge(effectiveTarget.id, effectiveSource.id, resolutions);
    if (result !== null) {
      handleClose();
    } else {
      setMergeError('La fusion a échoué. Veuillez réessayer.');
    }
  };

  const handleClose = () => {
    setStep('identification');
    setSourceClient(null);
    setKeepTarget(true);
    setResolutions({});
    setMergeError(null);
    onClose();
  };

  const previewClient = useMemo<Partial<Client>>(() => {
    if (!effectiveTarget || !effectiveSource) return {};
    const merged = { ...effectiveTarget };
    for (const [field, choice] of Object.entries(resolutions)) {
      const key = field as keyof Client;
      (merged as Record<string, unknown>)[field] = choice === 'source' ? effectiveSource[key] : effectiveTarget[key];
    }
    const fields = ['first_name', 'last_name', 'email', 'phone', 'notes'] as const;
    for (const f of fields) {
      if (effectiveTarget[f] === null && effectiveSource[f] !== null) {
        merged[f] = effectiveSource[f];
      }
    }
    return merged;
  }, [effectiveTarget, effectiveSource, resolutions]);

  const STEPS: { id: Step; label: string }[] = [
    { id: 'identification', label: 'Identification' },
    ...(conflicts.length > 0 ? [{ id: 'resolution' as Step, label: 'Résolution' }] : []),
    { id: 'confirmation', label: 'Confirmation' },
  ];

  const stepIndex = STEPS.findIndex((s) => s.id === step);
  const previewName = [previewClient.last_name, previewClient.first_name].filter(Boolean).join(' ') || previewClient.email || 'Client';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Fusionner deux fiches clients" size="lg">
      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-5">
        {STEPS.map((s, i) => (
          <div key={s.id} className="flex items-center gap-2">
            <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold transition-colors ${
              i < stepIndex ? 'bg-success-500 text-white' : i === stepIndex ? 'bg-primary-600 text-white' : 'bg-neutral-200 text-neutral-500'
            }`}>
              {i < stepIndex ? '✓' : i + 1}
            </div>
            <span className={`text-sm ${i === stepIndex ? 'font-semibold text-neutral-900' : 'text-neutral-400'}`}>
              {s.label}
            </span>
            {i < STEPS.length - 1 && <div className="w-8 h-px bg-neutral-200" />}
          </div>
        ))}
      </div>

      {/* Step 1: Identification */}
      {step === 'identification' && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-neutral-600">
            Sélectionnez la fiche doublon à fusionner avec <strong>{[targetClient.last_name, targetClient.first_name].filter(Boolean).join(' ') || targetClient.email || 'ce client'}</strong>.
          </p>
          <ClientSelectorForMerge
            userId={userId}
            excludeClientId={targetClient.id}
            onSelect={handleSelectSource}
          />
          {sourceClient && (
            <div className="flex flex-col gap-3 border-t border-neutral-100 pt-4">
              <div className="flex items-stretch gap-3">
                <ClientCard client={effectiveTarget!} role="Fiche à conserver" />
                <button
                  onClick={handleSwap}
                  title="Échanger les rôles"
                  className="flex-shrink-0 self-center p-2 rounded-lg hover:bg-neutral-100 text-neutral-400 hover:text-neutral-700 transition-colors"
                >
                  <ArrowLeftRight className="w-4 h-4" />
                </button>
                <ClientCard client={effectiveSource!} role="Fiche à fusionner" />
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={goToResolution}
                className="self-end"
              >
                Continuer
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Resolution */}
      {step === 'resolution' && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-neutral-600">
              Choisissez la valeur à conserver pour chaque champ en conflit.
            </p>
            <span className={`text-sm font-medium ${allResolved ? 'text-success-600' : 'text-neutral-500'}`}>
              {resolvedCount} / {conflicts.length} résolus
            </span>
          </div>
          <div>
            {conflicts.map((c) => (
              <FieldConflictRow
                key={c.field}
                conflict={c}
                choice={resolutions[c.field as keyof FieldResolutions]}
                onChoose={handleChoose}
              />
            ))}
          </div>
          <div className="flex gap-2 justify-between pt-2 border-t border-neutral-100">
            <Button variant="secondary" size="sm" onClick={() => setStep('identification')}>
              Retour
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setStep('confirmation')}
              disabled={!allResolved}
            >
              Voir l'aperçu
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Confirmation */}
      {step === 'confirmation' && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-neutral-600">
            Vérifiez la fiche finale avant de confirmer la fusion.
          </p>
          <div className="border border-neutral-200 rounded-xl p-4 bg-neutral-50">
            <p className="text-xs font-medium text-neutral-400 uppercase tracking-wide mb-3">
              Fiche fusionnée résultante
            </p>
            <div className="flex items-center gap-3 mb-3">
              <ClientAvatar displayName={previewName} size="md" />
              <div>
                <p className="text-sm font-semibold text-neutral-900">{previewName}</p>
              </div>
            </div>
            <dl className="space-y-1.5 text-sm">
              {[
                ['Email', previewClient.email],
                ['Téléphone', previewClient.phone],
                ['Notes', previewClient.notes],
              ].map(([label, value]) =>
                value ? (
                  <div key={label as string} className="flex gap-2">
                    <dt className="text-neutral-400 w-20 flex-shrink-0">{label}</dt>
                    <dd className="text-neutral-700 truncate">{value as string}</dd>
                  </div>
                ) : null
              )}
            </dl>
          </div>
          <div className="flex items-center gap-2 text-xs text-neutral-500 bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2">
            <ShieldCheck className="w-3.5 h-3.5 text-neutral-400 flex-shrink-0" />
            Cette opération est annulable pendant 30 jours depuis l'onglet Historique.
          </div>
          {mergeError && (
            <p className="text-sm text-error-600 bg-error-50 border border-error-200 rounded-lg px-3 py-2">
              {mergeError}
            </p>
          )}
          <div className="flex gap-2 justify-between pt-2 border-t border-neutral-100">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setStep(conflicts.length > 0 ? 'resolution' : 'identification')}
            >
              Retour
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleConfirm}
              disabled={merging}
            >
              {merging ? 'Fusion en cours...' : 'Fusionner les deux fiches'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
